<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="form-designer">
    <!-- 左侧：字段组件面板 -->
    <div class="field-panel">
      <div class="panel-header">
        <h3 class="text-sm font-semibold text-slate-700">表单字段</h3>
      </div>
      <div class="panel-content overflow-y-auto p-3">
        <div class="space-y-2">
          <div
            v-for="fieldType in fieldTypes"
            :key="fieldType.type"
            class="flex items-center gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-primary-300 rounded-lg cursor-grab active:cursor-grabbing transition-all"
            draggable="true"
            @dragstart="onFieldDragStart($event, fieldType)"
          >
            <el-icon :size="16" :class="fieldType.iconClass">
              <component :is="fieldType.icon" />
            </el-icon>
            <div class="flex-1">
              <div class="text-sm font-medium text-slate-800">{{ fieldType.label }}</div>
              <div class="text-xs text-slate-500">{{ fieldType.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 中间：表单画布 -->
    <div class="canvas-panel">
      <div class="panel-header px-4 py-3 bg-white border-b border-slate-200 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-slate-700">{{ formName || '未命名表单' }}</h3>
        <div class="flex gap-2">
          <el-button size="small" @click="previewForm">
            <el-icon><View /></el-icon>
            预览
          </el-button>
          <el-button size="small" type="primary" @click="saveForm">
            <el-icon><DocumentChecked /></el-icon>
            保存
          </el-button>
        </div>
      </div>

      <!-- 表单画布区域 -->
      <div class="canvas-content flex-1 overflow-auto p-6 min-h-0">
        <div
          class="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6 min-h-96"
          @drop="onFieldDrop"
          @dragover.prevent
        >
          <div v-if="formFields.length === 0" class="flex items-center justify-center h-80 text-slate-400">
            <div class="text-center">
              <el-icon :size="48" class="mb-2"><DocumentAdd /></el-icon>
              <p class="text-sm">从左侧拖拽字段到此处开始设计表单</p>
            </div>
          </div>

          <!-- 表单字段列表 -->
          <div v-else class="space-y-4">
            <div
              v-for="(field, index) in formFields"
              :key="field.id"
              class="group relative p-4 border rounded-lg transition-all"
              :class="[
                selectedField?.id === field.id
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              ]"
              @click="selectField(field)"
            >
              <!-- 字段操作按钮 -->
              <div class="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  class="w-6 h-6 flex items-center justify-center rounded bg-white border border-slate-300 hover:bg-slate-50"
                  @click.stop="moveFieldUp(index)"
                  :disabled="index === 0"
                >
                  <el-icon :size="12"><ArrowUp /></el-icon>
                </button>
                <button
                  class="w-6 h-6 flex items-center justify-center rounded bg-white border border-slate-300 hover:bg-slate-50"
                  @click.stop="moveFieldDown(index)"
                  :disabled="index === formFields.length - 1"
                >
                  <el-icon :size="12"><ArrowDown /></el-icon>
                </button>
                <button
                  class="w-6 h-6 flex items-center justify-center rounded bg-white border border-slate-300 hover:bg-red-50 hover:text-red-600"
                  @click.stop="removeField(index)"
                >
                  <el-icon :size="12"><Delete /></el-icon>
                </button>
              </div>

              <!-- 字段预览 -->
              <div class="pr-24">
                <label class="block text-sm font-medium text-slate-700 mb-1">
                  {{ field.label }}
                  <span v-if="field.required" class="text-red-500">*</span>
                </label>
                <p v-if="field.placeholder" class="text-xs text-slate-500 mb-2">{{ field.placeholder }}</p>

                <!-- 不同类型字段的预览 -->
                <div class="mt-2">
                  <!-- 文本输入 -->
                  <input
                    v-if="field.type === 'TEXT' || field.type === 'EMAIL' || field.type === 'PHONE'"
                    type="text"
                    :placeholder="field.placeholder"
                    class="form-input w-full"
                    disabled
                  />

                  <!-- 数字输入 -->
                  <input
                    v-else-if="field.type === 'NUMBER' || field.type === 'MONEY'"
                    type="number"
                    :placeholder="field.placeholder"
                    class="form-input w-full"
                    disabled
                  />

                  <!-- 多行文本 -->
                  <textarea
                    v-else-if="field.type === 'TEXTAREA'"
                    :placeholder="field.placeholder"
                    :rows="field.rows || 3"
                    class="form-textarea form-control-sm"
                    disabled
                  ></textarea>

                  <!-- 日期选择 -->
                  <input
                    v-else-if="field.type === 'DATE' || field.type === 'DATETIME'"
                    type="date"
                    class="form-input w-full"
                    disabled
                  />

                  <!-- 下拉选择 -->
                  <el-select
                    v-else-if="field.type === 'SELECT'"
                    class="form-select w-full"
                    :placeholder="field.placeholder || '请选择'"
                    disabled
                  >
                    <el-option v-for="opt in field.options" :key="opt.value" :label="opt.label" :value="opt.value" />
                  </el-select>

                  <!-- 单选框 -->
                  <div v-else-if="field.type === 'RADIO'" class="space-y-2">
                    <label
                      v-for="opt in field.options"
                      :key="opt.value"
                      class="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <input type="radio" :name="field.key" :value="opt.value" disabled class="text-primary-600" />
                      {{ opt.label }}
                    </label>
                  </div>

                  <!-- 多选框 -->
                  <div v-else-if="field.type === 'CHECKBOX'" class="space-y-2">
                    <label
                      v-for="opt in field.options"
                      :key="opt.value"
                      class="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <input type="checkbox" :value="opt.value" disabled class="text-primary-600" />
                      {{ opt.label }}
                    </label>
                  </div>

                  <!-- 文件上传 -->
                  <div v-else-if="field.type === 'FILE'" class="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center bg-slate-50">
                    <el-icon :size="32" class="text-slate-400"><Upload /></el-icon>
                    <p class="text-sm text-slate-500 mt-2">点击上传或拖拽文件到此处</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：字段属性配置 -->
    <div v-if="selectedField" class="props-panel">
      <div class="panel-header px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-slate-700">字段属性</h3>
        <button @click="selectedField = null" class="text-slate-400 hover:text-slate-600">
          <el-icon><Close /></el-icon>
        </button>
      </div>

      <div class="panel-content flex-1 overflow-y-auto p-4">
        <el-form label-position="top" size="small">
          <!-- 基础属性 -->
          <el-form-item label="字段标识">
            <el-input v-model="selectedField.key" placeholder="用于数据存储的唯一标识" />
          </el-form-item>

          <el-form-item label="字段标签">
            <el-input v-model="selectedField.label" placeholder="显示给用户的标签" />
          </el-form-item>

          <el-form-item label="占位提示">
            <el-input v-model="selectedField.placeholder" placeholder="输入框的提示文字" />
          </el-form-item>

          <el-form-item label="字段类型">
            <el-tag>{{ getFieldTypeLabel(selectedField.type) }}</el-tag>
          </el-form-item>

          <el-form-item label="是否必填">
            <el-switch v-model="selectedField.required" />
          </el-form-item>

          <el-form-item v-if="selectedField.type === 'TEXT'" label="最大长度">
            <el-input-number v-model="selectedField.maxLength" :min="1" :max="1000" class="form-number" />
          </el-form-item>

          <el-form-item v-if="selectedField.type === 'TEXTAREA'" label="行数">
            <el-input-number v-model="selectedField.rows" :min="2" :max="10" class="form-number" />
          </el-form-item>

          <el-form-item v-if="selectedField.type === 'NUMBER' || selectedField.type === 'MONEY'" label="最小值">
            <el-input-number v-model="selectedField.min" :controls="false" class="form-number" />
          </el-form-item>

          <el-form-item v-if="selectedField.type === 'NUMBER' || selectedField.type === 'MONEY'" label="最大值">
            <el-input-number v-model="selectedField.max" :controls="false" class="form-number" />
          </el-form-item>

          <!-- 选项配置（下拉、单选、多选） -->
          <template v-if="['SELECT', 'RADIO', 'CHECKBOX'].includes(selectedField.type)">
            <el-divider content-position="left">选项配置</el-divider>
            <div v-for="(option, index) in selectedField.options" :key="index" class="mb-3 p-3 bg-slate-50 rounded-lg">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-medium text-slate-700">选项 {{ index + 1 }}</span>
                <el-button
                  type="danger"
                  link
                  size="small"
                  @click="removeOption(index)"
                  :disabled="selectedField.options.length <= 1"
                >
                  删除
                </el-button>
              </div>
              <el-form-item label="选项标签">
                <el-input v-model="option.label" placeholder="显示文本" size="small" />
              </el-form-item>
              <el-form-item label="选项值">
                <el-input v-model="option.value" placeholder="实际值" size="small" />
              </el-form-item>
            </div>
            <el-button type="primary" link class="w-full" @click="addOption">
              <el-icon><Plus /></el-icon>
              添加选项
            </el-button>
          </template>

          <!-- 文件上传配置 -->
          <template v-if="selectedField.type === 'FILE'">
            <el-divider content-position="left">上传配置</el-divider>
            <el-form-item label="允许的文件类型">
              <el-input v-model="selectedField.accept" placeholder="如: .jpg,.png,.pdf" />
            </el-form-item>
            <el-form-item label="最大文件大小(MB)">
              <el-input-number v-model="selectedField.maxSize" :min="1" :max="100" class="form-number" />
            </el-form-item>
            <el-form-item label="最多上传数量">
              <el-input-number v-model="selectedField.maxCount" :min="1" :max="10" class="form-number" />
            </el-form-item>
          </template>

          <!-- 验证规则 -->
          <el-divider content-position="left">验证规则</el-divider>
          <el-form-item label="自定义验证">
            <el-input
              v-model="selectedField.validation"
              type="textarea"
              :rows="2"
              placeholder="正则表达式，如: ^[0-9]{11}$"
            />
          </el-form-item>
          <el-form-item label="验证提示">
            <el-input v-model="selectedField.validationMessage" placeholder="验证失败时的提示信息" />
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      v-model="previewVisible"
      title="表单预览"
      width="720px"
      class="preview-dialog"
      :show-close="true"
      :close-on-click-modal="true"
      destroy-on-close
    >
      <template #header>
        <div class="dialog-header">
          <div class="header-content">
            <el-icon class="header-icon"><View /></el-icon>
            <span class="header-title">表单预览</span>
          </div>
          <div class="header-tip">以下为表单的实际展示效果</div>
        </div>
      </template>

      <div class="preview-content">
        <div v-if="formFields.length === 0" class="empty-state">
          <el-icon :size="56" class="empty-icon"><DocumentAdd /></el-icon>
          <p class="empty-text">暂无表单字段</p>
          <p class="empty-tip">请先在左侧添加表单字段</p>
        </div>

        <div v-else class="form-fields-wrapper">
          <div class="form-header-info">
            <h4 class="form-title">{{ formName || '未命名表单' }}</h4>
            <span class="field-count">共 {{ formFields.length }} 个字段</span>
          </div>

          <el-form label-position="top" class="preview-form">
            <div
              v-for="field in formFields"
              :key="field.id"
              class="form-field-item"
            >
              <el-form-item :label="field.label" :required="field.required" class="field-label">
                <template #label>
                  <span class="label-text">
                    {{ field.label }}
                    <span v-if="field.required" class="required-mark">*</span>
                  </span>
                </template>
                <p v-if="field.placeholder" class="field-placeholder">{{ field.placeholder }}</p>

                <!-- 实际可交互的表单元素 -->
                <el-input
                  v-if="['TEXT', 'EMAIL', 'PHONE'].includes(field.type)"
                  :placeholder="field.placeholder || `请输入${field.label}`"
                  :maxlength="field.maxLength"
                  :show-word-limit="field.maxLength > 0"
                  clearable
                />
                <el-input-number
                  v-else-if="['NUMBER', 'MONEY'].includes(field.type)"
                  :placeholder="field.placeholder"
                  :min="field.min"
                  :max="field.max"
                  :precision="field.type === 'MONEY' ? 2 : 0"
                  class="form-number"
                />
                <el-input
                  v-else-if="field.type === 'TEXTAREA'"
                  type="textarea"
                  :placeholder="field.placeholder || `请输入${field.label}`"
                  :rows="field.rows || 3"
                  :maxlength="field.maxLength"
                  :show-word-limit="field.maxLength > 0"
                  resize="none"
                />
                <el-date-picker
                  v-else-if="field.type === 'DATE'"
                  type="date"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  class="w-full form-date"
                  format="YYYY年MM月DD日"
                  value-format="YYYY-MM-DD"
                />
                <el-date-picker
                  v-else-if="field.type === 'DATETIME'"
                  type="datetime"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  class="w-full form-date"
                  format="YYYY年MM月DD日 HH:mm"
                  value-format="YYYY-MM-DD HH:mm:ss"
                />
                <el-select
                  v-else-if="field.type === 'SELECT'"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  class="w-full form-select"
                  clearable
                >
                  <el-option
                    v-for="opt in field.options"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
                <el-radio-group v-else-if="field.type === 'RADIO'" class="radio-group">
                  <el-radio
                    v-for="opt in field.options"
                    :key="opt.value"
                    :label="opt.value"
                    class="radio-item"
                  >
                    {{ opt.label }}
                  </el-radio>
                </el-radio-group>
                <el-checkbox-group v-else-if="field.type === 'CHECKBOX'" class="checkbox-group">
                  <el-checkbox
                    v-for="opt in field.options"
                    :key="opt.value"
                    :label="opt.value"
                    class="checkbox-item"
                  >
                    {{ opt.label }}
                  </el-checkbox>
                </el-checkbox-group>
                <el-upload
                  v-else-if="field.type === 'FILE'"
                  :accept="field.accept || '*/*'"
                  :limit="field.maxCount || 5"
                  :max-size="(field.maxSize || 10) * 1024 * 1024"
                  drag
                  multiple
                  action="#"
                  :auto-upload="false"
                  class="file-upload-wrapper"
                >
                  <div class="upload-content">
                    <el-icon class="upload-icon"><UploadFilled /></el-icon>
                    <div class="upload-text">
                      <span class="primary">拖拽文件到此处</span>
                      <span class="secondary">或</span>
                      <em>点击上传</em>
                    </div>
                    <div class="upload-tip">
                      支持 {{ field.accept || '所有文件' }} 格式，单个文件不超过 {{ field.maxSize || 10 }}MB
                    </div>
                  </div>
                </el-upload>
              </el-form-item>
            </div>
          </el-form>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="previewVisible = false" class="btn-close">
            <el-icon><Close /></el-icon>
            关闭预览
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  Edit,
  Calendar,
  List,
  Document,
  Upload,
  Money,
  Phone,
  Message as MessageIcon,
  View,
  DocumentChecked,
  DocumentAdd,
  Close,
  Delete,
  ArrowUp,
  ArrowDown,
  Plus,
  UploadFilled
} from '@element-plus/icons-vue'

// Props
const props = defineProps({
  initialForm: {
    type: Object,
    default: () => null
  }
})

// Emits
const emit = defineEmits(['save', 'update'])

// 表单基本信息
const formName = ref(props.initialForm?.name || '新建表单')
const formFields = ref(props.initialForm?.fields || [])
const selectedField = ref(null)
const previewVisible = ref(false)

// 监听 initialForm 变化，同步数据
watch(() => props.initialForm, (newForm) => {
  if (newForm) {
    formName.value = newForm.name || '新建表单'
    formFields.value = newForm.fields || []
  }
}, { immediate: true })

// 字段类型定义
const fieldTypes = [
  {
    type: 'TEXT',
    label: '单行文本',
    description: '短文本输入',
    icon: Edit,
    iconClass: 'text-blue-600'
  },
  {
    type: 'TEXTAREA',
    label: '多行文本',
    description: '长文本输入',
    icon: Document,
    iconClass: 'text-blue-600'
  },
  {
    type: 'NUMBER',
    label: '数字',
    description: '数值输入',
    icon: Edit,
    iconClass: 'text-green-600'
  },
  {
    type: 'MONEY',
    label: '金额',
    description: '货币金额',
    icon: Money,
    iconClass: 'text-green-600'
  },
  {
    type: 'DATE',
    label: '日期',
    description: '日期选择',
    icon: Calendar,
    iconClass: 'text-purple-600'
  },
  {
    type: 'DATETIME',
    label: '日期时间',
    description: '日期时间选择',
    icon: Calendar,
    iconClass: 'text-purple-600'
  },
  {
    type: 'SELECT',
    label: '下拉选择',
    description: '单项选择',
    icon: List,
    iconClass: 'text-orange-600'
  },
  {
    type: 'RADIO',
    label: '单选框',
    description: '单项选择',
    icon: List,
    iconClass: 'text-orange-600'
  },
  {
    type: 'CHECKBOX',
    label: '多选框',
    description: '多项选择',
    icon: List,
    iconClass: 'text-orange-600'
  },
  {
    type: 'EMAIL',
    label: '邮箱',
    description: '邮箱地址',
    icon: MessageIcon,
    iconClass: 'text-cyan-600'
  },
  {
    type: 'PHONE',
    label: '手机号',
    description: '手机号码',
    icon: Phone,
    iconClass: 'text-cyan-600'
  },
  {
    type: 'FILE',
    label: '文件上传',
    description: '附件上传',
    icon: Upload,
    iconClass: 'text-red-600'
  }
]

// 拖拽开始
function onFieldDragStart(event, fieldType) {
  event.dataTransfer.setData('fieldType', JSON.stringify(fieldType))
}

// 放置字段
function onFieldDrop(event) {
  const fieldTypeData = event.dataTransfer.getData('fieldType')
  if (!fieldTypeData) return

  const fieldType = JSON.parse(fieldTypeData)
  const newField = createField(fieldType.type)
  formFields.value.push(newField)
  selectedField.value = newField
  
  ElMessage.success(`已添加${fieldType.label}字段`)
}

// 创建字段
function createField(type) {
  const fieldCount = formFields.value.filter(f => f.type === type).length
  const baseField = {
    id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    key: `${type.toLowerCase()}_${fieldCount + 1}`,
    label: `${getFieldTypeLabel(type)}${fieldCount + 1}`,
    placeholder: '',
    required: false,
    validation: '',
    validationMessage: ''
  }

  // 根据类型添加特定配置
  if (['SELECT', 'RADIO', 'CHECKBOX'].includes(type)) {
    baseField.options = [
      { label: '选项1', value: 'option1' },
      { label: '选项2', value: 'option2' }
    ]
  }

  if (type === 'TEXT') {
    baseField.maxLength = 100
  }

  if (type === 'TEXTAREA') {
    baseField.rows = 3
  }

  if (['NUMBER', 'MONEY'].includes(type)) {
    baseField.min = null
    baseField.max = null
  }

  if (type === 'FILE') {
    baseField.accept = ''
    baseField.maxSize = 10
    baseField.maxCount = 1
  }

  return baseField
}

// 选择字段
function selectField(field) {
  selectedField.value = field
}

// 移除字段
function removeField(index) {
  formFields.value.splice(index, 1)
  if (selectedField.value && selectedField.value.id === formFields.value[index]?.id) {
    selectedField.value = null
  }
  ElMessage.success('字段已删除')
}

// 上移字段
function moveFieldUp(index) {
  if (index === 0) return
  const temp = formFields.value[index]
  formFields.value[index] = formFields.value[index - 1]
  formFields.value[index - 1] = temp
}

// 下移字段
function moveFieldDown(index) {
  if (index === formFields.value.length - 1) return
  const temp = formFields.value[index]
  formFields.value[index] = formFields.value[index + 1]
  formFields.value[index + 1] = temp
}

// 添加选项
function addOption() {
  if (!selectedField.value || !selectedField.value.options) return
  const optionCount = selectedField.value.options.length
  selectedField.value.options.push({
    label: `选项${optionCount + 1}`,
    value: `option${optionCount + 1}`
  })
}

// 删除选项
function removeOption(index) {
  if (!selectedField.value || !selectedField.value.options) return
  if (selectedField.value.options.length <= 1) {
    ElMessage.warning('至少保留一个选项')
    return
  }
  selectedField.value.options.splice(index, 1)
}

// 获取字段类型标签
function getFieldTypeLabel(type) {
  const fieldType = fieldTypes.find(f => f.type === type)
  return fieldType ? fieldType.label : type
}

// 预览表单
function previewForm() {
  if (formFields.value.length === 0) {
    ElMessage.warning('请先添加表单字段')
    return
  }
  previewVisible.value = true
}

// 保存表单
function saveForm() {
  if (formFields.value.length === 0) {
    ElMessage.warning('请先添加表单字段')
    return
  }

  const formData = {
    name: formName.value,
    fields: formFields.value
  }

  emit('save', formData)
  ElMessage.success('表单保存成功')
}

// 获取表单数据（供父组件调用）
const getFormData = () => {
  return {
    name: formName.value,
    fields: formFields.value
  }
}

// 获取字段列表（供流程设计器使用）
const getFieldList = () => {
  return formFields.value.map(field => ({
    key: field.key,
    label: field.label,
    type: field.type
  }))
}

// 暴露方法给父组件
defineExpose({
  getFormData,
  getFieldList
})
</script>

<style scoped>
.form-designer {
  height: calc(85vh - 120px);
  min-height: 400px;
  width: 100%;
  overflow: hidden;
  display: flex;
}

/* 左侧字段面板 */
.field-panel {
  width: 256px;
  height: 100%;
  background: white;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.field-panel .panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
}

.field-panel .panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 中间画布区域 */
.canvas-panel {
  flex: 1;
  height: 100%;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.canvas-panel .panel-header {
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.canvas-panel .canvas-content {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

/* 右侧属性面板 */
.props-panel {
  width: 320px;
  height: 100%;
  background: white;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.props-panel .panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
}

.props-panel .panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 预览对话框样式 */
.preview-dialog {
  :deep(.el-dialog) {
    border-radius: 16px;
    overflow: hidden;
  }

  :deep(.el-dialog__header) {
    margin: 0;
    padding: 20px 24px 16px;
    background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  }

  :deep(.el-dialog__headerbtn) {
    top: 50%;
    transform: translateY(-50%);
    right: 16px;

    .el-dialog__close {
      color: rgba(255, 255, 255, 0.8);
      font-size: 18px;

      &:hover {
        color: #fff;
      }
    }
  }

  :deep(.el-dialog__body) {
    padding: 0;
    max-height: 70vh;
    overflow-y: auto;
  }

  :deep(.el-dialog__footer) {
    padding: 16px 24px;
    border-top: 1px solid #F1F5F9;
    background: #FAFAFA;
  }
}

.dialog-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  width: 22px;
  height: 22px;
  color: #fff;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 1px;
}

.header-tip {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin-left: 32px;
}

.preview-content {
  padding: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.empty-icon {
  color: #CBD5E1;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  color: #64748B;
  margin-bottom: 8px;
}

.empty-tip {
  font-size: 13px;
  color: #94A3B8;
}

.form-fields-wrapper {
  background: #FAFAFA;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  overflow: hidden;
}

.form-header-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
}

.form-title {
  font-size: 15px;
  font-weight: 600;
  color: #1E293B;
  margin: 0;
}

.field-count {
  font-size: 12px;
  color: #64748B;
  background: #E2E8F0;
  padding: 4px 10px;
  border-radius: 12px;
}

.preview-form {
  padding: 8px 0;

  :deep(.el-form-item) {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px dashed #E2E8F0;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #F8FAFC;
    }
  }

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}

.form-field-item {
  transition: background-color 0.2s ease;

  &:hover {
    background: #F8FAFC;
  }
}

.field-label {
  :deep(.el-form-item__label) {
    padding: 0;
    font-weight: 500;
    color: #334155;
    font-size: 14px;
    line-height: 1.5;
  }
}

.label-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-mark {
  color: #EF4444;
  font-weight: 600;
  margin-left: 2px;
}

.field-placeholder {
  font-size: 12px;
  color: #94A3B8;
  margin: 4px 0 8px;
  line-height: 1.4;
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.radio-item {
  padding: 6px 12px;
  border-radius: 6px;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  transition: all 0.2s ease;

  &:hover {
    background: #E2E8F0;
  }

  :deep(.el-radio__label) {
    font-size: 13px;
    color: #475569;
  }
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.checkbox-item {
  padding: 6px 12px;
  border-radius: 6px;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  transition: all 0.2s ease;

  &:hover {
    background: #E2E8F0;
  }

  :deep(.el-checkbox__label) {
    font-size: 13px;
    color: #475569;
  }
}

.file-upload-wrapper {
  :deep(.el-upload) {
    width: 100%;
  }

  :deep(.el-upload-dragger) {
    width: 100%;
    padding: 32px;
    border-radius: 12px;
    border: 2px dashed #CBD5E1;
    background: #FAFAFA;
    transition: all 0.3s ease;

    &:hover {
      border-color: #3B82F6;
      background: #F0F7FF;
    }
  }
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  width: 48px;
  height: 48px;
  color: #94A3B8;
  margin-bottom: 4px;
}

.upload-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;

  .primary {
    color: #475569;
    font-weight: 500;
  }

  .secondary {
    color: #94A3B8;
  }

  em {
    color: #3B82F6;
    font-style: normal;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

.upload-tip {
  font-size: 12px;
  color: #94A3B8;
  margin-top: 4px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.btn-close {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  color: #475569;
  transition: all 0.2s ease;

  &:hover {
    background: #E2E8F0;
    border-color: #CBD5E1;
    color: #334155;
  }

  .el-icon {
    font-size: 16px;
  }
}

/* 滚动条样式 */
.preview-content {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #CBD5E1;
    border-radius: 3px;

    &:hover {
      background: #94A3B8;
    }
  }
}
</style>
